import React from "react";
import { Section } from "../Global/Section/Section";
import styles from "./InfoBoxesModule.module.scss";
import { InfoboxesOptionsType } from "@/types/ModuleOptionsType";
import { Headline } from "../Global/Headline/Headline";
import { columnSize } from "@/utils/layout/columnSize.ts";
import { Modul_Infoboxen } from "@/wp/types/acf-module-types.ts";
import { useSublinesVisibility } from "@/hooks/useSublinesOverlinesVisibility.ts";
import { checkModuleIsHidden } from "@/utils/checkModuleIsHidden";
import { ImageType, LinkType, OptionsType } from "@/types/baseTypes";
import { ExtendedLinkType } from "../ExtededLink/ExtendedLink";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { TreatmentInfoType } from "@/types/OptionType.ts";
import * as motion from "motion/react-m";
import {
  stagger_variant_mid,
  y_variant_small,
} from "@/utils/animation/transitions";
export type InfoBoxesModuleType = Modul_Infoboxen;

export type InfoBoxElementType = {
  title: string;
  infobox: string;
  svg?: ImageType | false;
  link: LinkType;
  link_extended: ExtendedLinkType;
  tagline?: string;
};

export type TextInfoBoxType = {
  title: string;
  content: string;
  infobox: string;
  link: LinkType;
  link_extended: ExtendedLinkType;
  infobox_title: string;
};

interface InfoBoxesModuleProps {
  data: InfoBoxesModuleType;
  infoBoxOptions: InfoboxesOptionsType;
  treatmentInfos: OptionsType["acf"]["treatment_infos"];
}

type TreatmentInfoKeys = keyof Omit<TreatmentInfoType, "treatment_variants">;

const InfoBoxesModule = ({
  infoBoxOptions,
  data,
  treatmentInfos,
}: InfoBoxesModuleProps) => {
  const { getLabel } = useGlobalContext();
  const {
    headline,
    headline_html_tag,
    overline,
    subline,
    type,
    treatment_info,
  } = data.infoboxes;
  const { fields_to_show } = data.infoboxes as {
    fields_to_show: TreatmentInfoKeys[];
  };

  const treatmentInfo = treatmentInfos.find((info) => {
    return info.title == treatment_info;
  });

  const {
    id,
    background_color_class,
    section_margin_class,
    section_padding_class,
    module_width,
    // row_direction_class,
    headline_text_align_class,
    // text_align_class,
    // flex_direction_class,
    // icon_align_class,
    // grid_columns,
    // toggle_cardbox,
    // trenner_anzeigen,
  } = data.layout;

  const { show_overline_subline, columns /*, grid_columns_choices*/ } =
    infoBoxOptions ?? {
      show_overline_subline: "none",
      columns: { breakpoint: "tablet", number_of_cols: 12 },
      grid_columns_choices: {},
    };
  const { showOverline, showSubline } = useSublinesVisibility(
    show_overline_subline ?? "none",
  );

  const isHidden = checkModuleIsHidden(data.settings);
  if (isHidden) {
    return null;
  }

  const columnSizes =
    module_width !== "normal-frame"
      ? columns
        ? columnSize(columns)
        : "is-12 is-10-fullhd"
      : "is-12 is-10-fullhd";
  /*
    const fallbackItemWidth = {
      mobile: "100%",
      tablet: "50%",
      desktop: "50%",
      widescreen: "50%",
      fullhd: "50%",
      ultrahd: "50%",
    };
  */

  /*
    const itemWidth = getItemWidthBreakpoints(
      grid_columns_choices,
      grid_columns,
      fallbackItemWidth,
    );
  */

  const infoBoxesJSX = (
    <motion.div
      variants={stagger_variant_mid}
      initial="initial"
      whileInView="animate"
      viewport={{ once: true, amount: 0.2 }}
      className="columns is-multiline is-centered"
    >
      <motion.div variants={y_variant_small} className={`column is-12`}>
        <Headline
          headline={headline}
          headline_html_tag={headline_html_tag}
          overline={showOverline ? overline : ""}
          subline={showSubline ? subline : ""}
          extraClass={headline_text_align_class}
          wrapInSection={module_width === "frameless"}
          disableAnimation
        />
      </motion.div>
      <motion.div
        className={`columns column is-12 is-mobile is-multiline ${styles["text-infobox"]} gap-8`}
      >
        {fields_to_show.map((field) => {
          const HTag = !headline ? "h2" : "h3";
          const fieldLabel = getLabel("treatment_info_" + field);
          const value = treatmentInfo ? treatmentInfo[field] : undefined;
          return (
            <motion.div
              variants={y_variant_small}
              key={field}
              className={`column is-12-mobile ${styles.infobox} ${styles["infobox-module"]}`}
            >
              <HTag className={styles["infobox-title"]}>{fieldLabel}</HTag>
              <p className={styles["infobox-value"]}>{value}</p>
            </motion.div>
          );
        })}

        {/*{type === "text-infobox" && (
          <TextInfoBox
            text_infobox={text_infobox}
            columnsClass={`${tabletDirection} ${columns_vertical_align_class}`}
            fitHeight={
              columns_vertical_align_class === "is-vcentered" ? false : true
            }
            infoBoxOptions={infoBoxOptions}
            extraClass={text_align_class}
            show_cardbox={toggle_cardbox}
            show_divider={trenner_anzeigen}
            heading_level={headline ? 3 : 2}
          />
        )}

        {type !== "text-infobox" && (
          <InfoBoxes
            elements={elements || []}
            infoBoxOptions={infoBoxOptions}
            extraClass={`${text_align_class} ${icon_align_class} ${flex_direction_class}`}
            itemWidth={itemWidth}
            show_cardbox={toggle_cardbox}
            show_divider={trenner_anzeigen}
            heading_level={headline ? 3 : 2}
          />
        )}*/}
      </motion.div>
    </motion.div>
  );

  return (
    <Section
      id={id}
      className={`info-boxes ${styles["info-boxes"]}  ${type === "text-infobox" ? `${styles[type]}` : ""} ${background_color_class} ${section_padding_class} ${section_margin_class}`}
      disableColumns
      frameless={module_width === "frameless"}
    >
      {module_width === "narrow-frame" ? (
        <div className="columns is-centered">
          <div className={`column ${columnSizes} is-centered`}>
            {infoBoxesJSX}
          </div>
        </div>
      ) : (
        infoBoxesJSX
      )}
    </Section>
  );
};

export default InfoBoxesModule;
